import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRY_DIALS } from '../lib/countryDials';

const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const LIST = [...COUNTRY_DIALS].sort((a, b) => a.n.localeCompare(b.n, 'pt-BR'));

export const CountrySelect = ({ value, onChange, testIdPrefix = 'country', className = '', exclude = [] }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = useMemo(() => LIST.find((c) => c.n === value) || null, [value]);

  const filtered = useMemo(() => {
    const base = exclude.length ? LIST.filter((c) => !exclude.includes(c.n)) : LIST;
    const q = norm(query.trim());
    return q ? base.filter((c) => norm(c.n).includes(q)) : base;
  }, [query, exclude]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testIdPrefix}-select`}
          className={`flex h-10 w-full items-center gap-2 rounded-xl border border-slate-200 bg-white/70 px-3 text-left text-sm text-slate-700 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${className}`}>
          <span className="text-base leading-none">{selected ? selected.f : '🌍'}</span>
          <span className={`flex-1 truncate ${selected ? '' : 'text-slate-400'}`}>
            {selected ? selected.n : 'Selecione seu país'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 overflow-hidden rounded-xl p-0">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
          <Search className="h-4 w-4 flex-shrink-0 text-slate-400" />
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid={`${testIdPrefix}-search`} placeholder="Buscar país..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
        </div>
        <div className="max-h-60 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-slate-400">Nenhum país encontrado</p>
          )}
          {filtered.map((c) => (
            <button key={c.n} type="button"
              onClick={() => { onChange(c.n); setOpen(false); setQuery(''); }}
              data-testid={`${testIdPrefix}-option-${c.n}`}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-orange-50 ${c.n === value ? 'bg-orange-50/70 font-semibold text-orange-700' : 'text-slate-700'}`}>
              <span className="text-base leading-none">{c.f}</span>
              <span className="flex-1 truncate">{c.n}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CountrySelect;
